<template>
<div
    :class="['user-organization p-2 border-radius', freshAddedItems.includes(organization.id) ? 'freshly-added' : '']">
  <!--Downgrade warning-->
  <popup
      v-if="showDowngradeWarning"
      :elevated-z-index="10005"
      @close="showDowngradeWarning = null"
  >
    <div slot="popupMainContent">
      <delete-prompt
          :custom-message="$t('downgradeRoleWarning')"
          button-abort-text="abort"
          button-confirm-text="confirm"
          @abort="abortRoleEditing"
          @confirm="updateOrganizationRole(showDowngradeWarning, true)"
      />
    </div>
  </popup>

  <!-- Confirmation dialog for removal -->
  <popup
      v-if="showConfirmDialog"
      :elevated-z-index="10005"
      @close="showConfirmDialog = false"
  >
    <div slot="popupMainContent">
      <delete-prompt
          :custom-message="confirmMessage"
          button-abort-text="abort"
          button-confirm-text="confirm"
          @abort="showConfirmDialog = false"
          @confirm="$emit('removeOrganization', organization.id)"
      />
    </div>
  </popup>

  <div class="header d-flex justify-content-between align-items-center">
    <h3>{{ organizationName }}</h3>
    <div class="d-flex align-items-center">
      <div class="ml-3 d-flex">
        <div class="lighter uppercase mr-1">
          {{ $t("role") }}:
        </div>
        <role-selector
            :key="forceReRenderKey"
            class="role-selector"
            :pre-selected-role="organizationRole"
            :read-only="!hasEditPermission"
            @update="updateOrganizationRole"
        />
      </div>
      <div class="ml-3">
        <checkbox-widget
            :key="forceReRenderKey"
            :initial-value="organizationDefault"
            :label-text="$t('default')"
            :label-for="'default-' + organization.id"
            :disabled="!!organizationDefault || !hasEditPermission"
            @change="setDefault"
        />
      </div>
      <div
          v-if="hasEditPermission && !organizationDefault"
          @click="confirmRemoveOrganization"
          class="ml-2 clickable lighter">
        <icon
            size="1"
            type="close"
            class="pointer"
        />
      </div>
    </div>
  </div>
  <slot />
  <div
      v-if="showTeams"
      class="darker p-2 mt-2 border-radius">
    <div class="d-flex mb-2">
      <input
          v-model="teamSearchString"
          type="text"
          class="form-text form-text-dark col-6"
          :placeholder="$t('searchTeams')"
      />
      <universal-selector
          v-if="hasEditPermission"
          :select-placeholder="$t('addTeam')"
          :exclude-from-item-list="teams"
          :tag-condition="{
                      fieldName: 'visibility',
                      '0': 'user',
                    }"
          class="col-6 pr-0"
          filter-attribute="displayName"
          list-name="userTeamList"
          store-name="Team"
          :limit-by-organization-id="organization.id"
          @addItem="(item) => $emit('addTeam', item, organization.id)"
      />
    </div>
    <h4 class="uppercase lighter mt-2">{{ $t('teams') }}</h4>
    <div v-if="!filteredTeams || !filteredTeams.length" class="lighter mt-2">
      {{ $t('noTeams') }}
    </div>
    <user-team
        :class="['mb-1 pb-1 pt-1 pl-1 pr-1 border-thin-bottom border-radius', freshAddedItems.includes(team.id) ? 'freshly-added' : '']"
        v-for="team in filteredTeams"
        :key="team.id"
        :team="team"
        :organization-role="organizationRole"
        :read-only="!hasEditPermission"
        :user-id="userId"
        @updateTeamRole="(teamId, role) => $emit('updateTeamRole', teamId, role)"
        @removeTeam="(teamId) => $emit('removeTeam', {id: teamId, organizationId: organization.id})"
        @updateTeamDefault="(teamId, isDefault) => $emit('setDefaultTeam', teamId, isDefault)"
    />
  </div>
  <div
      class="row mt-1">
    <user-app-panel-editor
        class="col-12"
        :app-panel-url="appPanel"
        :app-panel-editing="false"
        :organization-id="organization.id"
        :read-only="!hasEditPermission"
        @updateAppPanel="(appPanel) => $emit('updateAppPanel', organization.id, appPanel)"
    />
  </div>
</div>
</template>
<script>
import RoleSelector from "@/components/organizations/RoleSelector.vue";
import UserTeam from "@/components/UserTeam.vue";
import Icon from "@/components/Icon.vue";
import UniversalSelector from "@/components/organizations/UniversalSelector.vue";
import UserAppPanelEditor from "@/components/forms/UserAppPanelEditor.vue";
import {UserRoles} from "@/enum";
import DeletePrompt from "@/components/forms/DeletePrompt.vue";
import Popup from "@/components/Popup.vue";
import CheckboxWidget from "@/components/widgets/editors/Checkbox.vue";
export default {
  name: "UserOrganization",
  components: {
    CheckboxWidget,
    Popup, DeletePrompt,
    UserAppPanelEditor,
    UniversalSelector,
    RoleSelector,
    UserTeam,
    Icon
  },
  props: {
    organization: {
      type: Object,
      required: true
    },
    showTeams: { type: Boolean, default: true },
    userId: { type: String, required: true },
    freshAddedItems: { type: Array, default: () => [] },
  },
  data() {
    return {
      teamSearchString: '',
      hasEditPermission: false,
      showDowngradeWarning: null,
      forceReRenderKey: 0,
      showConfirmDialog: false,
      confirmMessage: '',
    };
  },
  computed: {
    teams() {
      return this.organization.teams || [];
    },
    organizationName() {
      return this.organization.displayName;
    },
    organizationDefault() {
      const {members} = this.organization;
      if(members && members[0]) {
        const {relation} = members[0];
        if(relation) {
          return relation.default;
        }
      }
      return false;
    },
    organizationRole() {
      const {members} = this.organization;
      if(members && members[0]) {
        const {relation} = members[0];
        if(relation) {
          return relation.role;
        }
      }
      return 'none';
    },
    appPanel() {
      const {members} = this.organization;
      if(members && members[0]) {
        const {relation} = members[0];
        if(relation) {
          return relation.appPanel;
        }
      }
      return null;
    },
    filteredTeams() {
      if(!this.teamSearchString) {
        return this.teams;
      }
      return this.teams.filter(team => {
        return team.name.toLowerCase().includes(this.teamSearchString.toLowerCase());
      });
    },
  },
  watch: {
    organizationDefault() {
      this.forceReRenderKey++;
    },
  },
  mounted() {
    this.setOrgPermission();
  },
  methods: {
    confirmRemoveOrganization() {
      this.showConfirmDialog = true;
      this.confirmMessage = this.$t('confirmRemoveFrom', {
        type: 'Organization',
        name: this.$store.getters.getUserName(this.userId),
        itemName: this.organizationName
      });
    },
    async abortRoleEditing() {
      this.showDowngradeWarning = null;
      this.forceReRenderKey++;
    },
    async setOrgPermission() {
      this.hasEditPermission = await this.$store.dispatch('checkTeamOrgPermission', {
        op: 'writeOrganization',
        organizationId: this.organization.id
      });
    },
    setDefault() {
      if(!this.hasEditPermission || !!this.organizationDefault) {
        console.log('not setting org default')
        return;
      }
      console.log('i am firing default', this.organization.id);
      this.$emit("setDefault", this.organization.id, true);
    },
    roleIsDowngrade(role) {
      const rolesHierarchy = Object.values(UserRoles);
      const currentRoleIndex = rolesHierarchy.indexOf(this.organizationRole);
      const newRoleIndex = rolesHierarchy.indexOf(role);
      return newRoleIndex < currentRoleIndex;
    },
    isSameUser() {
      return this.$store.getters.getCurrentUserId === this.$route.params.id;
    },
    async updateOrganizationRole(role, downgradeChecked = false) {
      if(this.isSameUser() && this.roleIsDowngrade(role) && !downgradeChecked) {
        this.showDowngradeWarning = role;
        return;
      }
      if(this.organizationRole !== role) {
        this.$emit('updateOrganizationRole', this.organization.id, role);
      }
      this.showDowngradeWarning = null;
      await this.setOrgPermission();
    }
  }
}
</script>

<style lang="scss">
.freshly-added {
  background-color: $highlight;
}
</style>

<style scoped lang="scss">
.header {
  h3, .container {
    margin: 0;
  }
}
.border-thin-bottom:not(:last-child) {
  border-bottom: 1px solid rgba(255,255,255,0.1);
}
.user-organization {
  width: 100%;
  background-color: var(--vform-editor-ui-secondary-color);
}
.role-selector {
  max-width: 100px;
}
.disabled {
  cursor: not-allowed !important;
}
.app-panel-row {
  background-color: rgba(255,255,255,0.2);
  padding: 2px 4px;
}
.freshly-added {
  background-color: $highlight !important;
}
.no-pointer {
  pointer-events: none;
}
</style>